import React from "react";
import type { CategoryHeaderBlock } from "@/types/content";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { resolveHref } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./CategoryHeader.module.css";

export interface CategoryHeaderProps {
  block: CategoryHeaderBlock;
  className?: string;
}

/**
 * Category Header - the top of a product listing page: breadcrumb trail, title,
 * subtitle and the row of sibling-category pills with their product counts.
 *
 * Everything arrives resolved from the backend, which falls back to the page's
 * own name, intro text and child categories when the editor leaves the block's
 * fields empty — so dropping it on a category is enough to populate it.
 */
export const CategoryHeader: React.FC<CategoryHeaderProps> = ({
  block,
  className,
}) => {
  const localizedTexts = useLocalizedTexts();
  const { heading, subtitle, subtitleIsHtml, breadcrumbs, chips } = block;

  if (!heading) {
    return null;
  }

  const currentLocale = getLocale().language;
  const trail = breadcrumbs ?? [];
  const pills = chips ?? [];

  const containerClasses = [styles.header, className].filter(Boolean).join(" ");

  return (
    <section className={containerClasses} data-component="category-header">
      <div className={styles.inner}>
        {trail.length > 0 && (
          <nav
            className={styles.breadcrumb}
            aria-label={t(
              localizedTexts,
              "Tracker.Generic.Breadcrumb",
              "Breadcrumb"
            )}
          >
            <ol className={styles.breadcrumbList}>
              {trail.map((crumb, index) => {
                const isLast = index === trail.length - 1;
                return (
                  <li key={crumb.id || crumb.url} className={styles.crumb}>
                    {isLast ? (
                      <span className={styles.crumbCurrent} aria-current="page">
                        {crumb.name}
                      </span>
                    ) : (
                      <>
                        <a
                          className={styles.crumbLink}
                          href={resolveHref(crumb, currentLocale)}
                        >
                          {crumb.name}
                        </a>
                        <span
                          className={styles.crumbSeparator}
                          aria-hidden="true"
                        >
                          /
                        </span>
                      </>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        )}

        <h1 className={styles.title}>{heading}</h1>

        {subtitle &&
          (subtitleIsHtml ? (
            <div
              className={`${styles.subtitle} ${styles.subtitleHtml}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(subtitle) }}
            />
          ) : (
            <p className={styles.subtitle}>{subtitle}</p>
          ))}

        {pills.length > 0 && (
          <ul className={styles.chips}>
            {pills.map((chip) => {
              const href = chip.link
                ? resolveHref(chip.link, currentLocale)
                : null;
              const label = (
                <>
                  <span className={styles.chipLabel}>{chip.name}</span>
                  {typeof chip.count === "number" && (
                    <span className={styles.chipCount}>{chip.count}</span>
                  )}
                </>
              );

              return (
                <li key={chip.id} className={styles.chipItem}>
                  {href ? (
                    <a
                      className={styles.chip}
                      href={href}
                      data-active={chip.isActive ? "true" : undefined}
                      aria-current={chip.isActive ? "page" : undefined}
                    >
                      {label}
                    </a>
                  ) : (
                    <span
                      className={styles.chip}
                      data-active={chip.isActive ? "true" : undefined}
                      aria-current={chip.isActive ? "page" : undefined}
                    >
                      {label}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

export default CategoryHeader;
